import ArrowBack from "@mui/icons-material/ArrowBack";
import { GfgButton } from "@/components/ui/GfgButton";
import { GFG_LOGO } from "@/constants/campaignBuilder";
import type { Product } from "@/interfaces/product";
import { displayProductName } from "@/utils/product-helper";

interface RedemptionDetailsHeaderProps {
  /** The chosen gift — its image + name fill the phone-only gift bar. */
  product: Product;
  /** "Back to gifts" → the detail overlay / landing. */
  onBack: () => void;
  /** Partner logo URL when the campaign shows one; omit for GfG-only. */
  partnerLogoUrl?: string;
  /** Extra classes on the sticky root — used for the envelope hand-off fade (RFW-8). */
  className?: string;
}

/**
 * Recipient-details sticky top cluster (RFW-7): the GfG (+ optional partner) lockup
 * with a "Back to gifts" control, and a phone-only compact gift bar that stands in
 * for the summary card (hidden on phones).
 */
export default function RedemptionDetailsHeader({
  product,
  onBack,
  partnerLogoUrl,
  className,
}: RedemptionDetailsHeaderProps) {
  const overline = product.impactTag || product.cause;

  return (
    <div className={`sticky top-0 z-30 bg-white sm:static ${className ?? ""}`}>
      <div className="relative flex items-center justify-center border-card-border border-b px-4 py-2.5 sm:px-6 sm:py-3.5">
        <GfgButton
          variant="outline"
          aria-label="Back to gifts"
          className="-translate-y-1/2 absolute top-1/2 left-2 border-transparent bg-transparent max-sm:min-h-11 sm:left-5"
          startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
          onClick={onBack}
        >
          <span className="hidden sm:inline">Back to gifts</span>
        </GfgButton>
        <div className="flex items-center gap-2.5 sm:gap-4">
          <img src={GFG_LOGO} alt="Gifts for Good" className="block h-8.5 sm:h-12" />
          {partnerLogoUrl && (
            <>
              <div className="h-6.5 w-px bg-card-border sm:h-9" />
              {/* Same height as the GfG logo so both brands read at equal size (GFG-422). */}
              <img
                src={partnerLogoUrl}
                alt=""
                className="h-8.5 w-auto max-w-22 object-contain sm:h-12 sm:max-w-32"
              />
            </>
          )}
        </div>
      </div>
      {/* Compact chosen-gift bar — phone only (replaces the summary card). */}
      <div className="flex items-center gap-3 border-card-border border-b bg-clear-dust px-4 py-2.5 sm:hidden">
        <img
          src={product.imageUrl}
          alt=""
          className="h-16 w-16 shrink-0 rounded-card border border-card-border bg-white object-contain p-1"
        />
        <div className="min-w-0 flex-1">
          {overline && (
            <div className="mb-0.5 truncate font-lato text-[9.5px] font-bold uppercase tracking-[1.2px] text-dust-ink">
              {overline}
            </div>
          )}
          <div className="line-clamp-2 font-crimson text-[17px] leading-[1.15] text-rodeo-black">
            {displayProductName(product.name)}
          </div>
        </div>
      </div>
    </div>
  );
}
